import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { Plus, Edit, Trash2, X, Settings, List } from "lucide-react";
import { BlocoPerguntas, PerguntaFormulario } from '@/types/tarefas';
import { PerguntaFormularioEditor } from './PerguntaFormularioEditor';

export function BlocosPerguntasTab() {
  const [blocos, setBlocos] = useState<BlocoPerguntas[]>([]);
  const [loadingBlocos, setLoadingBlocos] = useState(false);
  const [showBlocoForm, setShowBlocoForm] = useState(false);
  const [editingBloco, setEditingBloco] = useState<BlocoPerguntas | null>(null);
  const [blocoFormData, setBlocoFormData] = useState<Partial<BlocoPerguntas>>({
    nome: '',
    descricao: '',
    perguntas: []
  });
  const [editingPergunta, setEditingPergunta] = useState<PerguntaFormulario | null>(null);
  const [showPerguntaForm, setShowPerguntaForm] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchBlocos();
  }, []);

  const fetchBlocos = async () => {
    try {
      setLoadingBlocos(true);
      const response = await fetchWithAuth('/api/blocos-perguntas');
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Resposta não é JSON:', text.substring(0, 200));
        throw new Error('Servidor retornou resposta inválida. Verifique se o servidor está rodando e reinicie-o se necessário.');
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || 'Erro ao buscar blocos');
      }
      
      const data: BlocoPerguntas[] = await response.json();
      setBlocos(data || []);
    } catch (error: any) {
      console.error('Erro ao buscar blocos:', error);
      if (error.message && error.message.includes('Servidor retornou resposta inválida')) {
        console.warn('Servidor pode não estar rodando ou precisa ser reiniciado');
        setBlocos([]);
      } else {
        toast({
          title: "Erro",
          description: error.message || "Não foi possível carregar os blocos de perguntas",
          variant: "destructive"
        });
      }
    } finally {
      setLoadingBlocos(false);
    }
  };

  const handleDelete = async (id: string, nome: string) => {
    if (!confirm(`Tem certeza que deseja excluir o bloco "${nome}"?`)) {
      return;
    }
    try {
      const response = await fetchWithAuth(`/api/blocos-perguntas/${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Erro ao excluir bloco');
      toast({
        title: "Sucesso",
        description: "Bloco excluído com sucesso",
      });
      fetchBlocos();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível excluir o bloco",
        variant: "destructive"
      });
    }
  };

  const handleSave = async () => {
    if (!blocoFormData.nome || !blocoFormData.perguntas || blocoFormData.perguntas.length === 0) {
      toast({
        title: "Erro",
        description: "Nome e pelo menos uma pergunta são obrigatórios",
        variant: "destructive"
      });
      return;
    }
    try {
      const url = editingBloco
        ? `/api/blocos-perguntas/${editingBloco.id}`
        : '/api/blocos-perguntas';
      const method = editingBloco ? 'PUT' : 'POST';
      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: blocoFormData.nome,
          descricao: blocoFormData.descricao || '',
          perguntas: blocoFormData.perguntas
        })
      });
      if (!response.ok) throw new Error('Erro ao salvar bloco');
      toast({
        title: "Sucesso",
        description: `Bloco ${editingBloco ? 'atualizado' : 'criado'} com sucesso`,
      });
      setShowBlocoForm(false);
      setEditingBloco(null);
      setBlocoFormData({ nome: '', descricao: '', perguntas: [] });
      fetchBlocos();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível salvar o bloco",
        variant: "destructive"
      });
    }
  };

  const handleCancel = () => {
    setShowBlocoForm(false);
    setEditingBloco(null);
    setBlocoFormData({ nome: '', descricao: '', perguntas: [] });
    setShowPerguntaForm(false);
    setEditingPergunta(null);
  };

  // Função para obter vínculo anterior (igual ao formulário)
  const obterVinculoAnterior = (
    perguntaId: string,
    perguntas: PerguntaFormulario[]
  ): { pergunta: PerguntaFormulario; tipo: 'multipla_escolha' | 'discursiva'; respostas: string[] } | null => {
    for (const pergunta of perguntas) {
      const regras = Array.isArray(pergunta.regras_navegacao) ? pergunta.regras_navegacao : [];
      const respostasVinculadas = regras
        .filter((regra) => regra.proxima_pergunta_id === perguntaId && regra.resposta)
        .map((regra) => regra.resposta!.trim())
        .filter((resposta) => resposta !== '');

      if (respostasVinculadas.length > 0 && pergunta.tipo_resposta === 'multipla_escolha') {
        return {
          pergunta,
          tipo: 'multipla_escolha',
          respostas: respostasVinculadas
        };
      }

      if (pergunta.proxima_pergunta_id === perguntaId) {
        return {
          pergunta,
          tipo: pergunta.tipo_resposta === 'multipla_escolha' ? 'multipla_escolha' : 'discursiva',
          respostas: []
        };
      }
    }

    return null;
  };

  // Função para obter label da pergunta (igual ao formulário)
  const getPerguntaLabel = (perguntaId: string, perguntasBase?: PerguntaFormulario[]) => {
    if (!perguntaId) return 'Encerrar formulário';
    const perguntas = perguntasBase || (blocoFormData.perguntas || [])
      .slice()
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
    const index = perguntas.findIndex((p) => p.id === perguntaId);
    const pergunta = perguntas.find((p) => p.id === perguntaId);
    if (!pergunta) {
      return 'Pergunta removida';
    }
    return index >= 0
      ? `Pergunta ${index + 1}: ${pergunta.texto}`
      : pergunta.texto;
  };

  const handleAddPergunta = () => {
    setEditingPergunta(null);
    setShowPerguntaForm(true);
  };

  const handleEditPergunta = (pergunta: PerguntaFormulario) => {
    // Criar uma cópia profunda da pergunta para evitar problemas de referência
    const perguntaEditada: PerguntaFormulario = {
      ...pergunta,
      opcoes: pergunta.opcoes ? [...pergunta.opcoes] : undefined,
      regras_navegacao: pergunta.regras_navegacao ? [...pergunta.regras_navegacao] : undefined
    };
    setEditingPergunta(perguntaEditada);
    setShowPerguntaForm(true);
  };

  const handleSavePergunta = (perguntaSalva: PerguntaFormulario, perguntaAnteriorId: string, respostasAnteriores: string[]) => {
    const perguntas = blocoFormData.perguntas || [];
    const estaEditando = Boolean(editingPergunta);
    const totalPerguntasAntes = perguntas.length;
    const estaCriandoPrimeiraPergunta = !estaEditando && totalPerguntasAntes === 0;
    const perguntaIdAtual = editingPergunta ? editingPergunta.id : perguntaSalva.id;
    const finalizaFluxo = perguntaSalva.finaliza_fluxo === true;

    // Remover vínculos existentes que apontam para esta pergunta
    let perguntasAtualizadas = perguntas.map((p) => {
      const regrasOriginais = Array.isArray(p.regras_navegacao) ? p.regras_navegacao : [];
      const regrasFiltradas = regrasOriginais.filter(
        (regra) => regra.proxima_pergunta_id !== perguntaIdAtual
      );
      const precisaAtualizarRegras = regrasFiltradas.length !== regrasOriginais.length;
      const precisaAtualizarNavegacao = p.proxima_pergunta_id === perguntaIdAtual;

      if (precisaAtualizarRegras || precisaAtualizarNavegacao) {
        return {
          ...p,
          regras_navegacao: p.regras_navegacao !== undefined ? regrasFiltradas : undefined,
          proxima_pergunta_id: precisaAtualizarNavegacao ? undefined : p.proxima_pergunta_id
        };
      }

      return p;
    });

    if (estaEditando) {
      perguntasAtualizadas = perguntasAtualizadas.map((p) =>
        p.id === perguntaIdAtual ? perguntaSalva : p
      );
    } else {
      perguntasAtualizadas = [...perguntasAtualizadas, perguntaSalva];
    }

    if (!estaCriandoPrimeiraPergunta && perguntaAnteriorId) {
      perguntasAtualizadas = perguntasAtualizadas.map((pergunta) => {
        if (pergunta.id !== perguntaAnteriorId) {
          return pergunta;
        }

        if (pergunta.tipo_resposta === 'multipla_escolha') {
          const respostasValidas = respostasAnteriores.filter((resposta) =>
            pergunta.opcoes?.includes(resposta)
          );
          const regrasExistentes = Array.isArray(pergunta.regras_navegacao)
            ? pergunta.regras_navegacao
            : [];
          
          // Remover regras existentes que apontam para esta pergunta
          const regrasFiltradas = regrasExistentes.filter(
            (regra) => {
              const naoApontaParaPerguntaAtual = regra.proxima_pergunta_id !== perguntaIdAtual;
              const respostaNaoSelecionada = !respostasValidas.includes(regra.resposta);
              
              return naoApontaParaPerguntaAtual || respostaNaoSelecionada;
            }
          );
          
          const novasRegras = [
            ...regrasFiltradas,
            ...respostasValidas.map((resposta) => ({
              resposta,
              proxima_pergunta_id: perguntaIdAtual
            }))
          ];

          return {
            ...pergunta,
            regras_navegacao: novasRegras
          };
        }

        return {
          ...pergunta,
          proxima_pergunta_id: perguntaIdAtual
        };
      });
    }

    setBlocoFormData({ ...blocoFormData, perguntas: perguntasAtualizadas });
    setShowPerguntaForm(false);
    setEditingPergunta(null);
  };

  const handleDeletePergunta = (perguntaId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta pergunta?')) {
      return;
    }
    const perguntas = blocoFormData.perguntas || [];

    // Remover vínculos existentes que apontam para esta pergunta
    const perguntasAtualizadas = perguntas.map((p) => {
      const regrasOriginais = Array.isArray(p.regras_navegacao) ? p.regras_navegacao : [];
      const regrasFiltradas = regrasOriginais.filter(
        (regra) => regra.proxima_pergunta_id !== perguntaId
      );
      const precisaAtualizarRegras = regrasFiltradas.length !== regrasOriginais.length;
      const precisaAtualizarNavegacao = p.proxima_pergunta_id === perguntaId;

      if (precisaAtualizarRegras || precisaAtualizarNavegacao) {
        return {
          ...p,
          regras_navegacao: p.regras_navegacao !== undefined ? regrasFiltradas : undefined,
          proxima_pergunta_id: precisaAtualizarNavegacao ? undefined : p.proxima_pergunta_id
        };
      }

      return p;
    });

    // Remover a pergunta da lista
    const novasPerguntas = perguntasAtualizadas.filter(p => p.id !== perguntaId);
    setBlocoFormData({ ...blocoFormData, perguntas: novasPerguntas });
  };

  const handleCancelPergunta = () => {
    setShowPerguntaForm(false);
    setEditingPergunta(null);
  };

  const getNumeroPerguntaVinculada = (perguntaId: string, perguntas: PerguntaFormulario[]) => {
    const vinculo = obterVinculoAnterior(perguntaId, perguntas);
    if (!vinculo) return null;

    const perguntasOrdenadas = perguntas.slice().sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
    const index = perguntasOrdenadas.findIndex((p) => p.id === vinculo.pergunta.id);
    return index >= 0 ? index + 1 : null;
  };

  return (
    <ScrollArea className="h-[600px] pr-4">
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Blocos de Perguntas</CardTitle>
                <CardDescription>
                  Crie blocos reutilizáveis de perguntas que podem ser vinculados a perguntas do formulário
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingBloco(null);
                  setBlocoFormData({ nome: '', descricao: '', perguntas: [] });
                  setShowBlocoForm(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Criar Bloco
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingBlocos ? (
              <div className="text-center py-8 text-muted-foreground">Carregando blocos...</div>
            ) : blocos.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border rounded-lg">
                <Settings className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Nenhum bloco criado ainda</p>
                <p className="text-xs mt-1">Clique em "Criar Bloco" para começar</p>
              </div>
            ) : (
              <div className="space-y-3">
                {blocos.map((bloco) => (
                  <Card key={bloco.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold">{bloco.nome}</span>
                          <Badge variant="outline" className="text-xs">
                            {bloco.perguntas?.length || 0} pergunta(s)
                          </Badge>
                        </div>
                        {bloco.descricao && (
                          <p className="text-sm text-muted-foreground mb-2">{bloco.descricao}</p>
                        )}
                        {bloco.perguntas && bloco.perguntas.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {bloco.perguntas.map((pergunta, idx) => (
                              <div key={pergunta.id} className="text-xs text-muted-foreground ml-4">
                                {idx + 1}. {pergunta.texto}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingBloco(bloco);
                            setBlocoFormData({
                              nome: bloco.nome,
                              descricao: bloco.descricao || '',
                              perguntas: bloco.perguntas || []
                            });
                            setShowBlocoForm(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(bloco.id, bloco.nome)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Formulário de Criar/Editar Bloco */}
        {showBlocoForm && (
          <Card className="p-4 border-2 border-primary">
            <CardHeader className="p-0 pb-4">
              <CardTitle className="text-base">
                {editingBloco ? 'Editar Bloco' : 'Novo Bloco'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 space-y-4">
              <div>
                <Label htmlFor="bloco-nome">Nome do Bloco *</Label>
                <Input
                  id="bloco-nome"
                  value={blocoFormData.nome}
                  onChange={(e) => setBlocoFormData({ ...blocoFormData, nome: e.target.value })}
                  placeholder="Ex: Perguntas sobre telhado"
                />
              </div>
              <div>
                <Label htmlFor="bloco-descricao">Descrição</Label>
                <Textarea
                  id="bloco-descricao"
                  value={blocoFormData.descricao || ''}
                  onChange={(e) => setBlocoFormData({ ...blocoFormData, descricao: e.target.value })}
                  placeholder="Descrição opcional do bloco"
                  rows={2}
                />
              </div>
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <List className="h-5 w-5" />
                      Perguntas do Bloco
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Crie perguntas com vínculos condicionais baseados em respostas anteriores
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddPergunta}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Pergunta
                  </Button>
                </div>
                {blocoFormData.perguntas && blocoFormData.perguntas.length > 0 ? (
                  <div className="space-y-3 mb-4">
                    {blocoFormData.perguntas
                      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
                      .map((pergunta, index) => {
                        const numeroVinculado = getNumeroPerguntaVinculada(pergunta.id, blocoFormData.perguntas || []);
                        return (
                          <Card key={pergunta.id} className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge variant="outline" className="text-xs font-semibold min-w-[2rem] text-center">
                                    {index + 1}
                                  </Badge>
                                  <span className="font-medium">{pergunta.texto}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {pergunta.tipo_resposta === 'multipla_escolha' ? 'Múltipla Escolha' : 'Discursiva'}
                                  </Badge>
                                  {numeroVinculado && (
                                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300">
                                      Vínculo pergunta {numeroVinculado}
                                    </Badge>
                                  )}
                                  {pergunta.finaliza_fluxo && (
                                    <Badge variant="outline" className="text-xs bg-rose-50 text-rose-700 border-rose-300">
                                      Fim
                                    </Badge>
                                  )}
                                </div>
                                {pergunta.tipo_resposta === 'multipla_escolha' && pergunta.opcoes && (
                                  <div className="ml-6 mt-2">
                                    <p className="text-xs text-muted-foreground mb-1">Opções:</p>
                                    <ul className="list-disc list-inside text-sm text-muted-foreground">
                                      {pergunta.opcoes.map((opcao, idx) => (
                                        <li key={idx}>{opcao}</li>
                                      ))}
                                    </ul>
                                    {Array.isArray(pergunta.regras_navegacao) && pergunta.regras_navegacao.length > 0 && (() => {
                                      // Filtrar apenas regras cujas respostas correspondem às opções desta pergunta
                                      const regrasValidas = pergunta.regras_navegacao.filter(regra => {
                                        const resposta = regra.resposta?.trim();
                                        if (!resposta) return false;
                                        return (pergunta.opcoes || []).some(opcao => opcao.trim() === resposta);
                                      });
                                      
                                      return regrasValidas.length > 0 ? (
                                        <div className="mt-2">
                                          <p className="text-xs text-muted-foreground mb-1">Encaminhamentos:</p>
                                          <ul className="text-xs text-muted-foreground space-y-1">
                                            {regrasValidas.map((regra, regraIdx) => (
                                              <li key={`${pergunta.id}-regra-${regraIdx}`}>
                                                <span className="font-semibold text-foreground">{regra.resposta || `Opção ${regraIdx + 1}`}</span>
                                                <span className="mx-1 text-muted-foreground">→</span>
                                                <span>
                                                  {regra.proxima_pergunta_id
                                                    ? getPerguntaLabel(regra.proxima_pergunta_id, blocoFormData.perguntas)
                                                    : 'Encerrar formulário'}
                                                </span>
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      ) : null;
                                    })()}
                                  </div>
                                )}
                                {/* Exibir encaminhamento para perguntas discursivas vinculadas a blocos */}
                                {pergunta.tipo_resposta === 'discursiva' && pergunta.proximo_bloco_id && (
                                  <div className="ml-6 mt-2">
                                    <div className="mt-2">
                                      <p className="text-xs text-muted-foreground mb-1">Encaminhamento:</p>
                                      <ul className="text-xs text-muted-foreground space-y-1">
                                        <li>
                                          <span className="font-semibold text-foreground">Pergunta discursiva</span>
                                          <span className="mx-1 text-muted-foreground">→</span>
                                          <span>
                                            {(() => {
                                              const blocoVinculado = blocos.find(b => b.id === pergunta.proximo_bloco_id);
                                              return blocoVinculado 
                                                ? `${blocoVinculado.nome} (bloco)`
                                                : 'Bloco';
                                            })()}
                                          </span>
                                        </li>
                                      </ul>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditPergunta(pergunta)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeletePergunta(pergunta.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground border rounded-lg">
                    <List className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Nenhuma pergunta adicionada ainda</p>
                    <p className="text-xs mt-1">Clique em "Adicionar Pergunta" para começar</p>
                  </div>
                )}

                {/* Formulário de Adicionar/Editar Pergunta */}
                {showPerguntaForm && (
                  <PerguntaFormularioEditor
                    editingPergunta={editingPergunta}
                    todasPerguntas={blocoFormData.perguntas || []}
                    onSave={handleSavePergunta}
                    onCancel={handleCancelPergunta}
                    obterVinculoAnterior={obterVinculoAnterior}
                    getPerguntaLabel={getPerguntaLabel}
                    isBlocoContext={true}
                  />
                )}
              </div>
              <div className="flex gap-2 justify-end pt-4 border-t mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={handleSave}
                >
                  {editingBloco ? 'Atualizar' : 'Criar'} Bloco
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </ScrollArea>
  );
}


